import { useCallback, useMemo } from 'react';
import { useLocalStorage, getTodayKey, getDateKey } from './useLocalStorage';

const DEFAULT_DAY_DATA = {
  // Morning - First interaction
  sleepQuality: '',
  wakeUpTime: '',
  wakeUpTimestamp: '',
  
  // Morning prompts
  gratitude: '',
  gratitudeComplete: false,
  whatWillMakeTodayGreat: '',
  intentionComplete: false,
  weight: '',
  feeling: 5,
  feelingSet: false,
  feelingWhy: '',
  
  // Tasks
  nonNegotiables: [],
  additionalTasks: [],
  todos: [],
  priorityTodos: [],
  tasksSkipped: false,
  
  // Morning Routine
  gotReady: false,
  dressed: false,
  readyToGo: false,
  
  // Productivity
  moneyMove: false,
  
  // Nighttime Routine
  brushedTeethPM: false,
  skinCarePM: false,
  bedTime: '',
  dayRating: 5,
  
  // Daily Habits (booleans)
  brushedTeethAM: false,
  brushedTeethPM: false,
  skinCareAM: false,
  skinCarePM: false,
  gym: false,
  nature: false,
  practicedSkill: false,
  decluttered: false,
  connectedWithSomeone: false,
  preppedClothes: false,
  
  // Reading/Listening
  readPages: 0,
  audibleMinutes: 0,
  currentBook: '',
  bookPagesLeft: '',
  
  // Water & Food
  waterOunces: 0,
  foodLog: [],
  
  // Evening
  bedTime: '',
  proudOf: '',
  dayRating: 5,
  improveTomorrow: '',
  
  // Meta
  completedAt: null,
  seenCompletionCelebration: false
};

export function useHabitData() {
  const [allData, setAllData] = useLocalStorage('habitStaking', {});
  const [settings, setSettings] = useLocalStorage('habitSettings', {
    wakeUpReminder: '06:00',
    windDownReminder: '20:00',
    bedtimeReminder: '22:00',
    waterGoalOunces: 128,
    readingGoalPages: 10,
    audibleGoalMinutes: 60,
    calorieGoal: 2000,
    notificationsEnabled: false
  });

  const todayKey = getTodayKey();
  
  const todayData = useMemo(() => {
    return allData[todayKey] || { ...DEFAULT_DAY_DATA };
  }, [allData, todayKey]);

  const updateToday = useCallback((updates) => {
    setAllData(prev => ({
      ...prev,
      [todayKey]: {
        ...(prev[todayKey] || DEFAULT_DAY_DATA),
        ...updates
      }
    }));
  }, [setAllData, todayKey]);

  const getDataForDate = useCallback((date) => {
    const key = getDateKey(date);
    return allData[key] || null;
  }, [allData]);

  const calculateStreak = useCallback(() => {
    let streak = 0;
    const today = new Date();
    
    for (let i = 0; i < 365; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() - i);
      const key = getDateKey(checkDate);
      const dayData = allData[key];
      
      if (dayData && calculateCompletionPercentage(dayData) >= 50) {
        streak++;
      } else if (i > 0) {
        break;
      }
    }
    
    return streak;
  }, [allData]);

  const calculateCompletionPercentage = useCallback((data = todayData) => {
    // Core trackable habits - only items the user can actually complete
    const habits = [
      // Morning routine
      data.brushedTeethAM,
      data.skinCareAM,
      data.gotReady,
      data.dressed,
      data.readyToGo,
      // Productivity & Growth
      data.gym,
      data.nature,
      data.practicedSkill,
      data.moneyMove, // Now a boolean
      data.decluttered,
      data.connectedWithSomeone,
      // Nighttime routine
      data.preppedClothes,
      data.brushedTeethPM,
      data.skinCarePM,
    ];
    
    // Add non-negotiables completion
    const nonNegCompleted = (data.nonNegotiables || []).filter(t => t.completed).length;
    const nonNegTotal = (data.nonNegotiables || []).length || 3; // Assume 3 if not set
    
    const habitsCompleted = habits.filter(Boolean).length;
    const totalItems = habits.length + nonNegTotal;
    const totalCompleted = habitsCompleted + nonNegCompleted;
    
    return Math.round((totalCompleted / totalItems) * 100);
  }, [todayData, settings]);

  const getLast7Days = useCallback(() => {
    const days = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const key = getDateKey(date);
      const data = allData[key];
      
      days.push({
        date: date,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        completion: data ? calculateCompletionPercentage(data) : 0,
        hasData: !!data
      });
    }
    
    return days;
  }, [allData, calculateCompletionPercentage]);

  return {
    todayData,
    updateToday,
    getDataForDate,
    calculateStreak,
    calculateCompletionPercentage,
    getLast7Days,
    settings,
    setSettings,
    allData
  };
}

export default useHabitData;
